import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { betaAPI, authAPI } from '../services/api';
import './SignupPage.css';

const BetaSignupPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const isSubmittingRef = useRef(false);
  const lastSubmissionTimeRef = useRef(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
  });

  // Check beta slots on mount
  useEffect(() => {
    sessionStorage.setItem('beta_signup', 'true');
    betaAPI.getStatus()
      .then((res) => {
        const data = res?.data || res;
        if (data?.is_closed || data?.is_full || data?.registration_enabled === false) {
          setIsFull(true);
        }
      })
      .catch(() => {})
      .finally(() => setCheckingStatus(false));
  }, []);

  const onSubmit = async (data) => {
    const now = Date.now();
    if (isSubmittingRef.current || loading) return;
    if (now - lastSubmissionTimeRef.current < 1000 && lastSubmissionTimeRef.current > 0) return;

    isSubmittingRef.current = true;
    lastSubmissionTimeRef.current = now;
    setLoading(true);

    try {
      const res = await betaAPI.register({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
      });

      const accessToken = res.access_token || res.data?.access_token;
      const refreshToken = res.refresh_token || res.data?.refresh_token;
      const userData = res.user || res.data?.user;

      if (accessToken) {
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        localStorage.removeItem('chat_session_id');
        localStorage.removeItem('chat_session_project_id');
        localStorage.removeItem('chat_messages');
        sessionStorage.removeItem('beta_signup');

        try {
          const meRes = await authAPI.getCurrentUser();
          const me = meRes?.data || meRes;
          if (me && typeof me === 'object') {
            sessionStorage.setItem('user_data', JSON.stringify(me));
            if (me.id) sessionStorage.setItem('user_id', String(me.id));
          } else if (userData) {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
            if (userData.id) sessionStorage.setItem('user_id', userData.id.toString());
          }
        } catch (_) {
          if (userData) {
            try {
              sessionStorage.setItem('user_data', JSON.stringify(userData));
              if (userData.id) sessionStorage.setItem('user_id', userData.id.toString());
            } catch (_) {}
          }
        }

        navigate('/create-avatar');
        return;
      }
    } catch (error) {
      const status = error?.status || error?.statusCode;
      if (status === 409) {
        // Beta is full
        setIsFull(true);
      } else if (status === 429) {
        toast.error('Too many attempts. Please wait a moment and try again.', { toastId: 'rate-limit' });
        setTimeout(() => {
          isSubmittingRef.current = false;
          lastSubmissionTimeRef.current = 0;
        }, 2000);
      } else {
        toast.error(error.message || 'Registration failed. Please try again.', { toastId: 'beta-reg-error' });
        isSubmittingRef.current = false;
        lastSubmissionTimeRef.current = 0;
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (isSubmittingRef.current) {
          isSubmittingRef.current = false;
          lastSubmissionTimeRef.current = 0;
        }
      }, 10000);
    }
  };

  if (checkingStatus) {
    return (
      <div className="signup-page">
        <Navbar />
        <LoadingSpinner fullScreen text="Checking beta availability..." />
      </div>
    );
  }

  if (isFull) {
    return (
      <div className="signup-page">
        <Navbar />
        <Container className="signup-container">
          <div className="signup-header">
            <h1>Beta Registration Closed</h1>
          </div>
          <div className="signup-form-wrapper" style={{ textAlign: 'center', padding: '32px 0' }}>
            <p style={{ fontSize: '17px', color: 'var(--slate, #7a6e68)', lineHeight: '1.6' }}>
              Beta registration is not available right now.
            </p>
            <p style={{ fontSize: '17px', color: 'var(--slate, #7a6e68)', lineHeight: '1.6' }}>
              If you'd like to be notified when space opens up, please email us at{' '}
              <a href="mailto:hello@sayagain.ai">hello@sayagain.ai</a>
            </p>
            <div style={{ marginTop: '24px' }}>
              <Link to="/signup" style={{ color: 'var(--soft-teal, #6b9ea0)', fontWeight: 600 }}>
                Sign up for a standard account
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <Navbar />

      {loading && <LoadingSpinner fullScreen text="Creating your account..." />}

      <Container className="signup-container">
        <div className="signup-header">
          <h1>Create Your Account</h1>
          <p style={{ fontSize: '15px', color: 'var(--soft-teal, #6b9ea0)', fontWeight: 600, marginTop: '6px' }}>
            You're joining the SayAgain beta — 60 days free.
          </p>
        </div>

        <div className="signup-form-wrapper">
          <Form onSubmit={handleSubmit(onSubmit)} className="signup-form">
            {/* Name Fields */}
            <div className="form-section">
              <label className="form-label-custom">Your Name <span className="required-asterisk">*</span></label>
              <Row>
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Control
                    type="text"
                    placeholder="First name"
                    className={`form-input ${errors.firstName ? 'is-invalid' : ''}`}
                    {...register('firstName', {
                      required: 'First name is required',
                      minLength: { value: 2, message: 'At least 2 characters' },
                    })}
                  />
                  {errors.firstName && <div className="error-message">{errors.firstName.message}</div>}
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Last name"
                    className={`form-input ${errors.lastName ? 'is-invalid' : ''}`}
                    {...register('lastName', {
                      required: 'Last name is required',
                      minLength: { value: 2, message: 'At least 2 characters' },
                    })}
                  />
                  {errors.lastName && <div className="error-message">{errors.lastName.message}</div>}
                </Col>
              </Row>
            </div>

            {/* Email Field */}
            <div className="form-section">
              <label className="form-label-custom">Email Address <span className="required-asterisk">*</span></label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
              {errors.email && <div className="error-message">{errors.email.message}</div>}
            </div>

            {/* Password Field */}
            <div className="form-section">
              <label className="form-label-custom">Password <span className="required-asterisk">*</span></label>
              <div className="password-input-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'At least 8 characters' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Must include uppercase, lowercase, and number',
                    },
                  })}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <div className="error-message">{errors.password.message}</div>}
            </div>

            <CustomButton
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={loading}
              className="signup-submit"
            >
              Join the Beta
            </CustomButton>

            <div className="signup-footer" style={{ marginTop: '20px' }}>
              Already have an account? <Link to="/signin">Sign in</Link>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default BetaSignupPage;
